import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  Bell,
  Mail,
  MessageSquare,
  Webhook,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Rss
} from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  getServices,
  getServiceStatus,
  getServiceDailyStatus,
  calculateUptime,
  getIncidentsByMonth,
  getActiveIncidents,
  type ServiceStatus,
  type DailyStatusData,
  type Incident,
} from '@/services/statusService';

interface ServiceData {
  id: string;
  name: string;
  status: ServiceStatus;
  uptime: number;
  dailyStatus: DailyStatusData[];
}

const statusConfig: Record<ServiceStatus, { color: string; bgColor: string; label: string; icon: typeof CheckCircle2 }> = {
  operational: { color: 'text-green-500', bgColor: 'bg-green-500', label: 'Operational', icon: CheckCircle2 },
  degraded: { color: 'text-yellow-500', bgColor: 'bg-yellow-500', label: 'Degraded Performance', icon: AlertTriangle },
  partial: { color: 'text-orange-500', bgColor: 'bg-orange-500', label: 'Partial Outage', icon: AlertTriangle },
  major: { color: 'text-red-500', bgColor: 'bg-red-500', label: 'Major Outage', icon: XCircle },
  maintenance: { color: 'text-blue-500', bgColor: 'bg-blue-500', label: 'Maintenance', icon: Clock },
};

// Global tooltip state
let activeTooltip: { data: DailyStatusData; rect: DOMRect } | null = null;
let tooltipListeners: (() => void)[] = [];

function setActiveTooltip(data: DailyStatusData | null, rect?: DOMRect) {
  if (data && rect) {
    activeTooltip = { data, rect };
  } else {
    activeTooltip = null;
  }
  tooltipListeners.forEach(fn => fn());
}

// Global Tooltip Portal Component
function TooltipPortal() {
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const listener = () => forceUpdate(n => n + 1);
    tooltipListeners.push(listener);
    return () => {
      tooltipListeners = tooltipListeners.filter(l => l !== listener);
    };
  }, []);

  if (!activeTooltip) return null;

  const { data, rect } = activeTooltip;
  const config = statusConfig[data.status];
  const StatusIcon = config.icon;

  const tooltipWidth = 300;
  let left = rect.left + rect.width / 2;
  let arrowOffset = 0;

  // Adjust if going off screen
  if (left - tooltipWidth / 2 < 16) {
    arrowOffset = left - tooltipWidth / 2 - 16;
    left = tooltipWidth / 2 + 16;
  } else if (left + tooltipWidth / 2 > window.innerWidth - 16) {
    arrowOffset = left + tooltipWidth / 2 - window.innerWidth + 16;
    left = window.innerWidth - tooltipWidth / 2 - 16;
  }

  return createPortal(
    <div
      className="fixed z-[99999] pointer-events-none"
      style={{
        left: left,
        top: rect.top - 12,
        transform: 'translate(-50%, -100%)'
      }}
    >
      <div className="bg-bg-card border border-border-default rounded-lg shadow-2xl p-4 w-[300px]">
        {/* Arrow */}
        <div
          className="absolute -bottom-[6px] left-1/2"
          style={{ transform: `translateX(calc(-50% + ${arrowOffset}px))` }}
        >
          <div className="w-3 h-3 bg-bg-card border-r border-b border-border-default transform rotate-45" />
        </div>

        {/* Date */}
        <div className="font-semibold text-text-primary mb-3">{data.date}</div>

        {/* Status info */}
        {data.status === 'operational' ? (
          <p className="text-text-secondary">No downtime recorded on this day.</p>
        ) : (
          <div className={cn(
            'flex items-center gap-3 px-3 py-2 rounded-lg',
            data.status === 'major' && 'bg-red-500/10',
            data.status === 'partial' && 'bg-orange-500/10',
            data.status === 'degraded' && 'bg-yellow-500/10',
            data.status === 'maintenance' && 'bg-blue-500/10'
          )}>
            <StatusIcon className={config.color} size={18} />
            <span className="font-medium">{config.label}</span>
            {data.duration && (
              <span className="text-text-secondary ml-auto">
                {data.duration.hours} hrs {data.duration.minutes} mins
              </span>
            )}
          </div>
        )}

        {/* Related incident */}
        {data.relatedIncident && (
          <>
            <div className="text-text-tertiary text-xs uppercase tracking-wide mt-4 mb-2">
              Related
            </div>
            <p className="text-text-secondary text-sm">{data.relatedIncident}</p>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

// Tooltip trigger component
interface StatusTooltipProps {
  data: DailyStatusData;
  children: React.ReactNode;
}

function StatusTooltip({ data, children }: StatusTooltipProps) {
  const ref = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setActiveTooltip(data, rect);
    }
  };

  const handleMouseLeave = () => {
    setActiveTooltip(null);
  };

  return (
    <div
      ref={ref}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="flex-1"
    >
      {children}
    </div>
  );
}

export function StatusPage() {
  const [expandedIncidents, setExpandedIncidents] = useState<string[]>([]);
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [subscribeType, setSubscribeType] = useState<'email' | 'slack' | 'webhook' | null>(null);
  const [servicesData, setServicesData] = useState<ServiceData[]>([]);
  const [pastIncidents, setPastIncidents] = useState<Record<string, Incident[]>>({});

  // Load data from service
  useEffect(() => {
    const loadData = () => {
      const services = getServices();
      const servicesWithStatus: ServiceData[] = services.map(service => ({
        id: service.id,
        name: service.name,
        status: getServiceStatus(service.id),
        uptime: calculateUptime(service.id),
        dailyStatus: getServiceDailyStatus(service.id),
      }));
      setServicesData(servicesWithStatus);
      setPastIncidents(getIncidentsByMonth());
    };

    loadData();
    // Refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Calculate overall status
  const overallStatus = servicesData.length === 0 ? 'operational' :
    servicesData.every(s => s.status === 'operational')
      ? 'operational'
      : servicesData.some(s => s.status === 'major')
        ? 'major'
        : 'degraded';

  const toggleIncident = (id: string) => {
    setExpandedIncidents(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  // Format date helper
  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const formatShortDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <>
      <TooltipPortal />
      <div className="pt-24 min-h-screen" style={{ backgroundColor: 'var(--color-bg-base)' }}>
      {/* Header */}
      <section className="section-sm border-b border-border-default">
        <div className="container max-w-4xl">
          <div className="text-center">
            {/* Overall Status */}
            <div className={cn(
              'inline-flex items-center gap-3 px-6 py-4 rounded-xl mb-6',
              overallStatus === 'operational' ? 'bg-green-500/10' :
              overallStatus === 'major' ? 'bg-red-500/10' : 'bg-yellow-500/10'
            )}>
              {overallStatus === 'operational' ? (
                <CheckCircle2 className="text-green-500" size={32} />
              ) : overallStatus === 'major' ? (
                <XCircle className="text-red-500" size={32} />
              ) : (
                <AlertTriangle className="text-yellow-500" size={32} />
              )}
              <span className={cn(
                'text-2xl font-semibold',
                overallStatus === 'operational' ? 'text-green-500' :
                overallStatus === 'major' ? 'text-red-500' : 'text-yellow-500'
              )}>
                {overallStatus === 'operational' ? 'All Systems Operational' :
                 overallStatus === 'major' ? 'Major System Outage' : 'Some Systems Degraded'}
              </span>
            </div>
            <p className="text-text-tertiary text-sm mb-4">
              Last updated: {new Date().toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZoneName: 'short'
              })}
            </p>
          </div>
        </div>
      </section>

      {/* Services Status */}
      <section className="section-sm">
        <div className="container max-w-4xl">
          <h2 className="text-lg font-semibold mb-6">Current Status</h2>

          <div className="space-y-4">
            {servicesData.map((service) => {
              const config = statusConfig[service.status];
              const StatusIcon = config.icon;

              return (
                <div
                  key={service.name}
                  className="card !p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <StatusIcon className={config.color} size={20} />
                      <span className="font-medium">{service.name}</span>
                    </div>
                    <span className={cn('text-sm', config.color)}>
                      {config.label}
                    </span>
                  </div>

                  {/* 90-day uptime visualization */}
                  <div className="flex items-center gap-[2px]">
                    {service.dailyStatus.map((dayData, i) => (
                      <StatusTooltip key={i} data={dayData}>
                        <div
                          className={cn(
                            'w-full h-8 rounded-sm transition-all hover:scale-y-125 cursor-pointer',
                            statusConfig[dayData.status].bgColor
                          )}
                        />
                      </StatusTooltip>
                    ))}
                  </div>

                  <div className="flex items-center justify-between mt-2 text-xs text-text-tertiary">
                    <span>90 days ago</span>
                    <span className="font-medium">{service.uptime}% uptime</span>
                    <span>Today</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Subscribe Section */}
      <section className="section-sm bg-bg-surface">
        <div className="container max-w-4xl">
          <div className="card">
            <div className="flex items-center gap-3 mb-4">
              <Bell className="text-accent" size={24} />
              <h2 className="text-lg font-semibold">Subscribe to Updates</h2>
            </div>
            <p className="text-text-secondary mb-6">
              Get notified when Ochtopus creates, updates or resolves an incident.
            </p>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setSubscribeType(subscribeType === 'email' ? null : 'email')}
                className={cn(
                  'btn btn-sm',
                  subscribeType === 'email' ? 'btn-primary' : 'btn-secondary'
                )}
              >
                <Mail size={16} />
                Email
              </button>
              <button
                onClick={() => setSubscribeType(subscribeType === 'slack' ? null : 'slack')}
                className={cn(
                  'btn btn-sm',
                  subscribeType === 'slack' ? 'btn-primary' : 'btn-secondary'
                )}
              >
                <MessageSquare size={16} />
                Slack
              </button>
              <button
                onClick={() => setSubscribeType(subscribeType === 'webhook' ? null : 'webhook')}
                className={cn(
                  'btn btn-sm',
                  subscribeType === 'webhook' ? 'btn-primary' : 'btn-secondary'
                )}
              >
                <Webhook size={16} />
                Webhook
              </button>
            </div>

            {subscribeType === 'email' && (
              <div className="mt-4 p-4 rounded-lg bg-bg-elevated">
                <form className="flex gap-3" onSubmit={(e) => e.preventDefault()}>
                  <input
                    type="email"
                    value={subscribeEmail}
                    onChange={(e) => setSubscribeEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="flex-1 px-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
                  />
                  <button type="submit" className="btn btn-primary">
                    Subscribe
                  </button>
                </form>
                <p className="text-text-tertiary text-xs mt-2">
                  We'll only send you updates about incidents and scheduled maintenance.
                </p>
              </div>
            )}

            {subscribeType === 'slack' && (
              <div className="mt-4 p-4 rounded-lg bg-bg-elevated">
                <p className="text-text-secondary mb-3">
                  Add Ochtopus Status to your Slack workspace to receive real-time updates.
                </p>
                <button className="btn btn-secondary">
                  <ExternalLink size={16} />
                  Add to Slack
                </button>
              </div>
            )}

            {subscribeType === 'webhook' && (
              <div className="mt-4 p-4 rounded-lg bg-bg-elevated">
                <form className="flex gap-3" onSubmit={(e) => e.preventDefault()}>
                  <input
                    type="url"
                    placeholder="https://your-webhook-url.com/status"
                    className="flex-1 px-4 py-2 rounded-lg bg-bg-card border border-border-default focus:border-accent focus:outline-none"
                  />
                  <button type="submit" className="btn btn-primary">
                    Add Webhook
                  </button>
                </form>
                <p className="text-text-tertiary text-xs mt-2">
                  We'll POST JSON updates to your webhook URL when incidents occur.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Incident History */}
      <section className="section-sm">
        <div className="container max-w-4xl">
          <h2 className="text-lg font-semibold mb-6">Past Incidents</h2>

          {Object.entries(pastIncidents).map(([month, incidents]) => (
            <div key={month} className="mb-8">
              <h3 className="text-text-secondary font-medium mb-4 pb-2 border-b border-border-default">
                {month}
              </h3>

              <div className="space-y-4">
                {incidents.map((incident) => (
                  <div key={incident.id} className="border border-border-default rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleIncident(incident.id)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-bg-card transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {expandedIncidents.includes(incident.id) ? (
                          <ChevronDown size={18} className="text-text-tertiary" />
                        ) : (
                          <ChevronRight size={18} className="text-text-tertiary" />
                        )}
                        <span className="font-medium">{incident.title}</span>
                        <span className={cn(
                          'text-xs px-2 py-0.5 rounded-full',
                          incident.impact === 'minor' && 'bg-yellow-500/20 text-yellow-500',
                          incident.impact === 'major' && 'bg-orange-500/20 text-orange-500',
                          incident.impact === 'critical' && 'bg-red-500/20 text-red-500',
                          incident.impact === 'maintenance' && 'bg-blue-500/20 text-blue-500'
                        )}>
                          {incident.impact}
                        </span>
                      </div>
                      <span className="text-text-tertiary text-sm">{formatShortDate(incident.createdAt)}</span>
                    </button>

                    {expandedIncidents.includes(incident.id) && (
                      <div className="px-4 pb-4 border-t border-border-default">
                        <div className="pt-4 space-y-4">
                          {incident.updates.map((update, i) => (
                            <div key={update.id || i} className="flex gap-4">
                              <div className="flex flex-col items-center">
                                <div className={cn(
                                  'w-2 h-2 rounded-full mt-2',
                                  update.status === 'Resolved' || update.status === 'Completed' ? 'bg-green-500' :
                                  update.status === 'Investigating' ? 'bg-yellow-500' :
                                  update.status === 'Identified' ? 'bg-orange-500' :
                                  'bg-blue-500'
                                )} />
                                {i < incident.updates.length - 1 && (
                                  <div className="w-px flex-1 bg-border-default mt-1" />
                                )}
                              </div>
                              <div className="flex-1 pb-4">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={cn(
                                    'font-medium text-sm',
                                    update.status === 'Resolved' || update.status === 'Completed' ? 'text-green-500' :
                                    update.status === 'Investigating' ? 'text-yellow-500' :
                                    update.status === 'Identified' ? 'text-orange-500' :
                                    'text-blue-500'
                                  )}>
                                    {update.status}
                                  </span>
                                  <span className="text-text-tertiary text-xs">
                                    {formatDate(update.timestamp)}
                                  </span>
                                </div>
                                <p className="text-text-secondary text-sm">
                                  {update.message}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* No incidents placeholder */}
          <div className="text-center py-8 text-text-tertiary">
            <CheckCircle2 className="mx-auto mb-3 text-green-500" size={32} />
            <p>No incidents reported for earlier months.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <section className="py-8 border-t border-border-default">
        <div className="container max-w-4xl">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-text-tertiary">
            <div className="flex items-center gap-4">
              <a href="/docs" className="hover:text-accent transition-colors">
                Documentation
              </a>
              <a href="https://ochtopus.com" className="hover:text-accent transition-colors flex items-center gap-1">
                <ExternalLink size={14} />
                ochtopus.com
              </a>
            </div>
            <div className="flex items-center gap-4">
              <a href="#" className="hover:text-accent transition-colors flex items-center gap-1">
                <Rss size={14} />
                RSS Feed
              </a>
              <a href="#" className="hover:text-accent transition-colors flex items-center gap-1">
                <Rss size={14} />
                Atom Feed
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
